import { decodeString } from "@findemdev/findem-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { IOrgV2 } from "../../../../../types/orgs";

const MIN_ORG_SEARCH_LENGTH = 2;

export interface IProjectClientOrgOption {
  accountId: string;
  label: string;
  name: string;
  ref: string;
  value: string;
}

interface IUseProjectClientOrgOptionsArgs {
  orgs?: IOrgV2[];
  visible: boolean;
}

const getOrgLabel = ({ org }: { org: IOrgV2 }): string =>
  decodeString(org.org_name || org.name || org.email || org.account_id);

const getSearchableOrgText = ({ org }: { org: IOrgV2 }): string =>
  [
    org.account_id,
    org.email,
    org.name,
    org.org_name,
    getOrgLabel({ org }),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const useProjectClientOrgOptions = ({
  orgs,
  visible,
}: IUseProjectClientOrgOptionsArgs) => {
  const [searchValue, setSearchValue] = useState("");

  const resetOptions = useCallback(() => {
    setSearchValue("");
  }, []);

  const searchOrgs = useCallback(({ searchValue }: { searchValue: string }) => {
    setSearchValue(searchValue);
  }, []);

  useEffect(() => {
    if (!visible) {
      resetOptions();
    }
  }, [resetOptions, visible]);

  const options = useMemo<IProjectClientOrgOption[]>(() => {
    const trimmedSearchValue = searchValue.trim().toLowerCase();

    if (!visible || trimmedSearchValue.length < MIN_ORG_SEARCH_LENGTH) {
      return [];
    }

    const optionsByAccountId = new Map<string, IProjectClientOrgOption>();

    (orgs || []).forEach((org) => {
      if (!org.account_id || optionsByAccountId.has(org.account_id)) return;
      if (!getSearchableOrgText({ org }).includes(trimmedSearchValue)) return;

      const label = getOrgLabel({ org });

      optionsByAccountId.set(org.account_id, {
        accountId: org.account_id,
        label,
        name: label,
        ref: org.account_id,
        value: org.account_id,
      });
    });

    return Array.from(optionsByAccountId.values()).sort((left, right) =>
      left.label.localeCompare(right.label),
    );
  }, [orgs, searchValue, visible]);

  return {
    isLoading: false,
    options,
    resetOptions,
    searchOrgs,
  };
};
